import { useSearchParams } from "@solidjs/router";
import { type Component, createMemo, For, onMount, Show } from "solid-js";
import { useI18n } from "@/api/i18n";
import { Container } from "@/components/shared/Container";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  PROPOSAL_STATE,
  type ProposalState,
} from "@/domains/application/constant/status";
import { useGetReceivedProposals } from "../api/proposals";
import type { ReceivedProposalItem } from "../types/proposals";
import { ProposalCard } from "./ProposalCard";

const PROPOSAL_TAB_ALL = "all" as const;

type ProposalTabValue = typeof PROPOSAL_TAB_ALL | ProposalState;

const PROPOSAL_TAB_VALUES: ProposalTabValue[] = [
  PROPOSAL_TAB_ALL,
  ...PROPOSAL_STATE,
];

function getProposalTabParam(
  raw: string | string[] | undefined,
): string | undefined {
  if (raw == null) return undefined;
  return Array.isArray(raw) ? raw[0] : raw;
}

function isValidProposalTab(
  value: string | undefined,
): value is ProposalTabValue {
  if (value === PROPOSAL_TAB_ALL) return true;
  return PROPOSAL_STATE.includes(value as ProposalState);
}

const ProposalsEmptyFallback: Component<{
  status?: ProposalTabValue;
}> = (props) => {
  const { t } = useI18n();
  const title = () =>
    props.status
      ? t("profile.tabs.proposals.empty.for_status.title")
      : t("profile.tabs.proposals.empty.title");
  const description = () =>
    props.status
      ? t("profile.tabs.proposals.empty.for_status.description")
      : t("profile.tabs.proposals.empty.description");
  return (
    <div class="text-center p-12 space-y-4">
      <div class="text-6xl mb-4">&#x1F4E8;</div>
      <div class="text-gray-600 text-lg font-medium">{title()}</div>
      <div class="text-gray-500 text-sm">{description()}</div>
    </div>
  );
};

export const ProposalsTab: Component = () => {
  const { t } = useI18n();
  const [searchParams, setSearchParams] = useSearchParams();

  const currentProposalTab = createMemo((): ProposalTabValue => {
    const raw = getProposalTabParam(searchParams.proposal_tab);
    return isValidProposalTab(raw) ? raw : PROPOSAL_TAB_ALL;
  });

  const onProposalTabChange = (value: ProposalTabValue) => {
    setSearchParams({ proposal_tab: value });
  };

  onMount(() => {
    const raw = getProposalTabParam(searchParams.proposal_tab);
    if (!isValidProposalTab(raw)) {
      setSearchParams({ proposal_tab: PROPOSAL_TAB_ALL });
    }
  });

  const activeFilter = createMemo(() => {
    const tab = currentProposalTab();
    return tab === PROPOSAL_TAB_ALL
      ? {}
      : { proposal_state: tab as ProposalState };
  });

  const proposals = useGetReceivedProposals(activeFilter);

  const allProposals = createMemo((): ReceivedProposalItem[] => {
    return proposals.data?.pages.flatMap((page) => page.data) ?? [];
  });

  return (
    <Container size="full">
      <div class="flex flex-col gap-6">
        <div class="flex items-center justify-between">
          <h1 class="text-2xl font-bold text-gray-900">
            {t("profile.tabs.proposals.title")}
          </h1>
        </div>

        <Show when={proposals.isLoading}>
          <div class="flex items-center justify-center p-8">
            <div class="text-gray-600 text-lg">{t("common.loading")}</div>
          </div>
        </Show>

        <Show when={proposals.isError}>
          <div class="flex items-center justify-center p-8">
            <div class="text-red-600 text-center">
              <div class="text-lg font-medium mb-2">
                {t("profile.tabs.proposals.error")}
              </div>
            </div>
          </div>
        </Show>

        <Show when={proposals.data}>
          <Tabs
            value={currentProposalTab()}
            onChange={(value) => onProposalTabChange(value as ProposalTabValue)}
            class="w-full"
          >
            <TabsList class="overflow-x-auto flex flex-nowrap gap-1 border-0 p-0">
              <For each={PROPOSAL_TAB_VALUES}>
                {(tabValue) => (
                  <TabsTrigger
                    value={tabValue}
                    class="shrink-0 max-w-[70%] md:shrink"
                  >
                    {tabValue === PROPOSAL_TAB_ALL
                      ? t("profile.tabs.proposals.status_tabs.all")
                      : t(`profile.tabs.proposals.status_tabs.${tabValue}`)}
                  </TabsTrigger>
                )}
              </For>
            </TabsList>

            <TabsContent value={currentProposalTab()} class="mt-4">
              <Show
                when={allProposals().length > 0}
                fallback={
                  <ProposalsEmptyFallback
                    status={
                      currentProposalTab() === PROPOSAL_TAB_ALL
                        ? undefined
                        : currentProposalTab()
                    }
                  />
                }
              >
                <div class="flex flex-col gap-4">
                  <For each={allProposals()}>
                    {(proposal) => <ProposalCard proposal={proposal} />}
                  </For>
                  <Show when={proposals.hasNextPage}>
                    <div class="flex justify-center">
                      <Button
                        variant="outline"
                        onClick={() => proposals.fetchNextPage()}
                        disabled={proposals.isFetchingNextPage}
                      >
                        {proposals.isFetchingNextPage
                          ? t("common.loading")
                          : t("profile.tabs.proposals.loadMore")}
                      </Button>
                    </div>
                  </Show>
                </div>
              </Show>
            </TabsContent>
          </Tabs>
        </Show>
      </div>
    </Container>
  );
};
